import React, { useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import SegmentOutlinedIcon from "@mui/icons-material/SegmentOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddIcon from "@mui/icons-material/Add";
import { FormProvider, RHFFileField, RHFRadioGroup, RHFSelectField, RHFTextField } from "../../common/hook-form";
import { useForm } from "react-hook-form";
import { escalationArray, generateRandom10DigitNumber } from "../../helpers/UtilityHelper";
import _ from "lodash";
import { LoadingButton } from "@mui/lab";
import { apiConstants, post } from "../../common/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useEffect } from "react";
import RHFTextArea from "../../common/hook-form/RHFTextArea";
import { useSnackbar } from "notistack";
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from "../../redux/commonApiCalls";
import { getToken } from "../../helpers/ReduxHelpers";
import jwt_decode from "jwt-decode";

// ── Direction B — sectioned "PCIS Health" look (soft cards + tinted icon-badge headers) ──
const NAVY = "#212B36";
const MUTED = "#637381";
const BLUE_GRAD = "linear-gradient(135deg, #4F7DF0 0%, #2065D1 100%)";
const cardSx = { border: "1px solid #E7EAF0", borderRadius: "14px", bgcolor: "#fff", boxShadow: "0 1px 2px rgba(15,23,42,.05)", overflow: "hidden" };
const cardHeadSx = { display: "flex", alignItems: "center", gap: 1.25, px: 2, py: 1.5, borderBottom: "1px solid #E7EAF0", bgcolor: "#F9FAFB" };
const cardIcSx = { width: 28, height: 28, borderRadius: "8px", display: "grid", placeItems: "center", bgcolor: "rgba(32,101,209,.08)", color: "#2065D1", flex: "0 0 auto", "& svg": { fontSize: 17 } };
const gradientBtnSx = {
    textTransform: "none", fontWeight: 700, borderRadius: "10px", px: 2.75,
    backgroundImage: BLUE_GRAD, boxShadow: "0 6px 16px rgba(32,101,209,.35)",
    "&:hover": { backgroundImage: "linear-gradient(135deg,#4470e6,#1b57bd)", boxShadow: "0 6px 16px rgba(32,101,209,.45)" },
    "&.Mui-disabled": { backgroundImage: "none", bgcolor: "#E2E8F0", color: "#94A3B8", boxShadow: "none" },
};
const resetBtnSx = { textTransform: "none", fontWeight: 600, borderRadius: "10px", color: MUTED, borderColor: "#CBD5E1", "&:hover": { borderColor: "#94A3B8", bgcolor: "#F8FAFC" } };

const SectionCard = ({ icon, title, sub, children }) => (
    <Box sx={cardSx}>
        <Box sx={cardHeadSx}>
            <Box sx={cardIcSx}>{icon}</Box>
            <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY }}>{title}</Typography>
                {sub && <Typography sx={{ fontSize: 11.5, color: MUTED }}>{sub}</Typography>}
            </Box>
        </Box>
        <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
);

const SummaryItem = ({ k, v }) => (
    <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "#8A94A6" }}>{k}</Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, mt: "1px" }}>{v}</Typography>
    </Box>
);

const CreateTicket = ({ setFiltersChange, handleOpen, setTicketId, statusList }) => {
    const [isLoading, setIsLoading] = useState(false)
    const [formFields, setFormFields] = useState([])
    const [dueDate, setDueDate] = useState("")
    const [frDueDate, setFrDueDate] = useState("")
    const { enqueueSnackbar } = useSnackbar();
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    const userToken = jwt_decode(getToken());
    const merchantId = userToken && userToken ? userToken : "";

    const formElementNames = formFields?.map((val) => ({
        name: val.name,
        isMandatory: val.isMandatory,
        type: val.type
    })) ?? []

    const createValidationSchema = (formElementNames) => {
        const schemaObject = {};
        formElementNames?.forEach((element, index) => {
            const key = element.type === 'file' ? `${element.name}_${index}` : element.name;
            if (element.isMandatory === 'Y' && element.type === 'file') {
                schemaObject[key] = Yup.mixed().required(`Please upload file`);
            } else if (element.isMandatory === 'Y' && element.type !== 'file') {
                schemaObject[key] = Yup.string().required(`Please provide ${element.name}`);
            }
            else if (key === "add_email") {
                schemaObject['add_email'] = Yup.string().test(
                    'valid-emails',
                    'Invalid email address',
                    (value) => {
                        if (!value) return true;
                        const emails = value.split(',').map(email => email.trim());
                        return emails.every(email => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email));
                    }
                );
            }
        });
        return Yup.object().shape(schemaObject);
    };

    const validationSchema = createValidationSchema([
        ...formElementNames,
        { name: 'category', isMandatory: 'Y' },
        { name: 'subcategory', isMandatory: 'Y' },
        { name: 'merchant', isMandatory: 'Y' },
        { name: 'add_email', isMandatory: 'N' },
    ]);

    const methods = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            category: '',
            subcategory: '',
            merchant: '',
            add_email: ''
        },
        shouldUnregister: true
    });

    const { handleSubmit, reset, watch, setValue } = methods;

    const selectedCategory = watch("category");
    const selectedSubcategory = watch("subcategory");
    const selectedMerchant = watch("merchant");
    const merchantName = merchants?.find((m) => m.id === selectedMerchant)?.name || "—";
    const data = escalationArray.filter((val) => val.categoryTitle);
    const categoryOptions = _.orderBy(data, "name").map((val) => ({
        value: val.categoryTitle,
        label: val.categoryTitle,
    }));

    const subcategoryOptions =
        selectedCategory &&
        data.find((val) => val.categoryTitle === selectedCategory)?.subCategories.map((subCat) => ({
            value: subCat.subCatTitle,
            label: subCat.subCatTitle,
        }));


    useEffect(() => {
        if (selectedSubcategory) {
            const formData =
                selectedCategory &&
                data.find((val) => val.categoryTitle === selectedCategory)?.subCategories.find(
                    (subCat) => subCat.subCatTitle === selectedSubcategory
                );
            const newFormFields = formData?.formElements || [];
            setDueDate(formData?.due)
            setFrDueDate(formData?.frDue)
            setFormFields(newFormFields);
            reset();
            newFormFields.forEach((elem, index) => {
                if (elem.type !== "file") {
                    setValue(elem.name, '');
                } else {
                    setValue(`${elem.name}_${index}`, null);
                }
            });
            setValue("category", selectedCategory);
            setValue("subcategory", selectedSubcategory);
            setValue("merchant", selectedMerchant);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSubcategory, selectedCategory]);



    const buildFormData = (data, awbNumber, cpId) => {
        const merchantSelectId = data?.merchant
        const merchant = merchants?.find((m) => m.id === merchantSelectId);
        let formData = new FormData();
        let descriptionString = data.description ? data.description : 'No Description';

        const ccEmails = data.add_email ? data.add_email.split(',') : [];
        ccEmails.forEach(email => {
            formData.append('cc_emails[]', email.trim());
        });

        for (const key in data) {
            if (key !== 'email' && key !== 'subject' && key !== 'status' && key !== 'priority' &&
                key !== 'category' && key !== 'awbNumber' && key !== 'merchantId' &&
                key !== 'subcategory' && key !== 'description' && !key.startsWith('uploadedFile_')) {
                descriptionString += `|${key} ${data[key]}`;
            }
        }
        formData.append('description', descriptionString);
        for (const key in data) {
            if (key.startsWith('uploadedFile_') && data[key] !== undefined && data[key] !== null) {
                formData.append('attachments', data[key][0]);
            }
        }
        // formData.append('cc_emails', data.add_email);
        formData.append('email', merchantId?.name);
        formData.append('name', merchantId?.fullname);
        formData.append('custom_fields[cf_user_email]', merchantId?.name);
        formData.append('custom_fields[cf_user_name]', merchantId?.fullname);
        formData.append('subject', `${data.category} / ${data.subcategory} / ${awbNumber ? awbNumber : generateRandom10DigitNumber()}`);
        formData.append('status', 2);
        formData.append('priority', 2);
        formData.append('frDueBy', frDueDate ? frDueDate : 2);
        formData.append('dueBy', dueDate ? dueDate : 24);
        formData.append('custom_fields[cf_category]', data.category);
        if (awbNumber) {
            formData.append('custom_fields[cf_awb_number387695]', awbNumber);
        }
        formData.append('custom_fields[cf_sub_category]', data.subcategory);
        formData.append('custom_fields[cf_merchant_id]', data?.merchant);
        if (cpId && cpId !== null) {
            formData.append('custom_fields[cf_cp_id]', cpId);
        }
        formData.append('custom_fields[cf_merchant_name]', merchant ? merchant.name : '-');
        formData.append('tags', data?.merchant ? data?.merchant.toString().substring(0, 32) : '-');
        cpId && (formData.append('tags', cpId?.toString().substring(0, 32)));
        // formData.append('tags', data.category ? data.category : '-');
        // formData.append('tags', data.subcategory ? data.subcategory : '-');
        // formData.append('tags', cpId ? cpId : '-');
        return formData;
    };


    const checkExistingTicket = async (awbNumber, category, subcategory) => {
        const statusListIds = statusList?.map(status => status.id).filter(status => status !== 4 && status !== 5);
        try {
            const request = {
                awbnumber_list: [awbNumber],
                category_list: [category],
                subcategory_list: [subcategory],
                status_list: statusListIds, // Open and Pending status
                page: 1,
                limit: 1
            };
            const response = await post(`${apiConstants.FILTER_FRESHDESK}`, request);
            const parsedRes = JSON.parse(response.data?.responseObj);
            return parsedRes?.total > 0;
        } catch (error) {
            console.error('Error checking existing ticket:', error);
            return false;
        }
    };

    const onSubmit = async (data) => {
        const merchantSelectId = data?.merchant
        setIsLoading(true)
        try {
            if (data.awbNumber) {
                const awbNumbers = data.awbNumber.split(',').map(num => num.trim());
                const uniqueAWBNumbers = Array.from(new Set(awbNumbers));
                for (const awb of uniqueAWBNumbers) {
                    // Check if ticket already exists for this AWB, category, and subcategory
                    const existingTicket = await checkExistingTicket(awb, data.category, data.subcategory);
                    if (existingTicket) {
                        enqueueSnackbar(`Ticket already exists for AWB ${awb} with category "${data.category}" and subcategory "${data.subcategory}"`, { variant: 'error' });
                        continue;
                    }

                    let d = await fetchAWBData(awb, merchantSelectId)
                    if (!d || d === null) {
                        enqueueSnackbar(`AWB Number ${awb} invalid or doesnt belong to this merchant`, { variant: 'error' })
                    } else {
                        let cpId = d;
                        const formData = buildFormData(data, awb, cpId);
                        submitForm(formData);
                    }
                }
            } else {
                const formData = buildFormData(data, null);
                submitForm(formData);
            }
            setIsLoading(false);
        } catch (err) {
            enqueueSnackbar("Some error occurred!", { variant: 'error' })
            setIsLoading(false);
            console.log(err);
        }
    };

    const submitForm = async (formData) => {
        try {
            const res = await post(`${apiConstants.CREATE_FRESHDESK}`, formData);
            if (res.data?.status === "ERROR") {
                enqueueSnackbar(res.data?.responseObj || "Some error occurred!", { variant: 'error' })
            } else {
                const parsedData = JSON.parse(res.data?.responseObj);
                setTicketId(parsedData?.id);
                handleOpen();
                reset();
                setFormFields([]);
                setFiltersChange(filterChange => filterChange + 1);
            }
            // setIsLoading(false);
        } catch (err) {
            enqueueSnackbar("Some error occurred!", { variant: 'error' })
            setIsLoading(false);
            console.log(err);
        }
    };

    const fetchAWBData = async (awb, merchantSelectId) => {
        try {
            const andfilter = [
                {
                    "field": "awbNumber",
                    "operator": "eq",
                    "value": awb.trim()
                }
            ];
            const includes = "merchantDetail,courierDetail";
            let d = { "andfilter": andfilter, "includes": includes };
            const res = await post(apiConstants.ORDER_SEARCH, d); // Wait for the post request to complete
            if (res.status !== 200 || res?.data?.count === 0) {
                return null;
            } else {
                let data = res.data?.data[0];
                let merchantFromData = data?.merchantDetail.key;
                if (merchantFromData !== merchantSelectId) {
                    return null;
                } else {
                    return data?.courierDetail?.prozoId;
                }
            }
        } catch (error) {
            console.error('Error fetching AWB data:', error);
            return null; // Return null if there's an issue fetching the AWB data
        }
    };


    // style width:100% on the <form> — the SideDrawer wraps children in a flex column with
    // alignItems:center, which otherwise shrinks the form to its content width and centres it.
    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
            {/* Soft tinted ground so the white section cards read as raised. Negative mx/mt bleed
                over the SideDrawer's 30px / 10px content padding so it fills the drawer edge-to-edge. */}
            <Box sx={{
                mx: "-30px", mt: "-26px", px: "30px", pt: 3, pb: 3, minHeight: 300,
                background: "radial-gradient(900px 320px at 85% -10%, rgba(32,101,209,.06), transparent 60%), #F7F8FA",
                display: "flex", flexDirection: "column", gap: 2,
            }}>

                {/* Selection summary + SLA — appears once fully classified */}
                {selectedMerchant && selectedCategory && selectedSubcategory && (
                    <Box sx={{
                        display: "flex", flexWrap: "wrap", gap: "10px 20px", alignItems: "center",
                        p: "12px 16px", borderRadius: "12px", border: "1px solid #E7EAF0",
                        background: "linear-gradient(135deg, rgba(32,101,209,.08), transparent 70%)",
                    }}>
                        <SummaryItem k="Merchant" v={merchantName} />
                        <SummaryItem k="Category" v={selectedCategory} />
                        <SummaryItem k="Subcategory" v={selectedSubcategory} />
                        <Box sx={{
                            ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.75,
                            border: "1px solid #F3D9AE", bgcolor: "#FDF3E5", borderRadius: "999px", px: 1.25, py: 0.5,
                        }}>
                            <AccessTimeIcon sx={{ fontSize: 14, color: "#B45309" }} />
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#B45309" }}>
                                FR {frDueDate || 2}h &middot; Resolve {dueDate || 24}h
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Section 1 — Classification */}
                <SectionCard icon={<SegmentOutlinedIcon />} title="Classification" sub="Who the ticket is for and what it's about">
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <RHFSelectField width="100%" name="merchant" label={`Select Merchant`} options={_.orderBy(merchants, "name").map((val) => ({
                                value: val.id,
                                label: val.name
                            }))}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RHFSelectField label="Category" name="category" options={categoryOptions} width="100%" />
                        </Grid>
                        {selectedCategory && (
                            <Grid item xs={12}>
                                <RHFSelectField label="Subcategory" name="subcategory" options={subcategoryOptions} width="100%" />
                            </Grid>
                        )}
                    </Grid>
                </SectionCard>

                {/* Section 2 — Ticket details (dynamic fields for the chosen subcategory) */}
                {formFields.length > 0 && (
                    <SectionCard icon={<AssignmentOutlinedIcon />} title="Ticket Details" sub={`Fields for “${selectedSubcategory}”`}>
                        <Grid container spacing={2}>
                            <FormFields formFields={formFields} />
                        </Grid>
                    </SectionCard>
                )}

                {/* Section 3 — Notifications */}
                {(selectedCategory && selectedSubcategory) && (
                    <SectionCard icon={<MailOutlineIcon />} title="Notifications" sub="Optional — copy people on this ticket">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={8}>
                                <RHFTextField label="Additional Email (Comma Separated)" name="add_email" />
                            </Grid>
                        </Grid>
                    </SectionCard>
                )}

                {/* Empty-state hint before a subcategory is chosen */}
                {formFields.length === 0 && (
                    <Typography sx={{ fontSize: 12.5, color: MUTED, px: 0.5 }}>
                        Select a merchant, category, and subcategory to load this ticket's fields.
                    </Typography>
                )}

                {/* Footer actions */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pt: 0.5 }}>
                    <Button variant="outlined" sx={resetBtnSx} disabled={formFields.length === 0} onClick={() => {
                        reset()
                        setFormFields([])
                    }}>
                        Reset
                    </Button>
                    <LoadingButton loading={isLoading} type="submit" variant="contained" sx={gradientBtnSx} disabled={formFields.length === 0} startIcon={<AddIcon />}>
                        Create Ticket
                    </LoadingButton>
                </Box>
            </Box>
        </FormProvider>
    );
};

export default CreateTicket


const FormFields = ({ formFields }) => {
    return (
        <>
            {formFields?.map((formElement, index) => (
                <React.Fragment key={`${formElement.subcategory}_${index}`}>
                    {formElement.type === "text" && (
                        <Grid item key={index} xs={12} md={6}>
                            <>
                                <RHFTextField
                                    label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                    name={formElement.name}
                                    fullWidth
                                />
                            </>
                        </Grid>
                    )}
                    {formElement.type === "textarea" && (
                        <Grid item key={index} xs={12} md={12}>
                            <>
                                <RHFTextArea
                                    // label={formElement.title}
                                    label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                    name={formElement.name}
                                    rows={4}
                                    fullWidth
                                />
                            </>
                        </Grid>
                    )}
                    {formElement.type === "file" && (
                        <Grid item key={index} xs={12} md={6}>
                            <RHFFileField
                                type="file"
                                label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                name={`${formElement.name}_${index}`}
                            />
                        </Grid>
                    )}
                    {formElement.type === "radio" && (
                        <Grid item key={index} xs={12} md={6}>
                            <RHFRadioGroup
                                type="radio"
                                label={formElement.isMandatory === "Y" ? `${formElement.title} *` : formElement.title}
                                name={formElement.name}
                                options={formElement.options.map((val) => {
                                    return {
                                        label: val, value: val
                                    }
                                })}
                            />
                        </Grid>
                    )}
                </React.Fragment>
            ))
            }
        </>
    )
}
export { FormFields }
